import * as React from "react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { stylesheet } from "typestyle";
import { useBoxes, useOccupancies, usePlatform } from "./hooks/context.js";
import { MaterialIcon } from "./icon.js";
import { layout } from "./layout.js";
import { ListItem, ListItemDescription, ListItemIcon, ListItemName, ListItemText } from "./list.js";
import { classNames, toT } from "./utils.js";
import { computeCubeUtilization } from "@variocube/app-common";
const SUPPORTED_BOX_FEATURES = ["cooled", "accessible", "dangerousGoods", "charger"];
export function BoxTypeSelection({ types, onSelect, className, strings, typeBoxDimensions, features: pFeatures }) {
    const platform = usePlatform();
    const t = toT(strings);
    const [features, setFeatures] = useState(() => new Set());
    const boxes = useBoxes();
    const occupancies = useOccupancies();
    const utilization = useMemo(() => computeCubeUtilization(boxes, occupancies, [...features]), [boxes, occupancies, features]);
    const availableFeatures = useMemo(() => {
        const { utilization } = platform;
        return SUPPORTED_BOX_FEATURES.filter(feat => getFeatureTotal(utilization, feat) > 0);
    }, []);
    const availableTypes = useMemo(() => {
        return new Set(Object.entries(utilization.types)
            .filter(([_, u]) => u.available)
            .map(([type, _]) => type));
    }, [utilization]);
    useEffect(() => {
        // if pre-selected features are provided
        if (pFeatures && pFeatures.length > 0) {
            // computed the available box types for the pre-selected box features combination
            const availableTypes = computeCubeUtilization(boxes, occupancies, [...pFeatures.filter(f => availableFeatures.includes(f))]);
            // select the box features checkbox accordingly
            if (Object.entries(availableTypes.types).filter(([_, u]) => u.available).length > 0) {
                const features = pFeatures.filter(f => availableFeatures.includes(f));
                setFeatures(new Set(features));
            }
        }
    }, [pFeatures, availableFeatures]);
    function handleFeatureClick(feature) {
        const nextFeatures = new Set(features);
        nextFeatures[nextFeatures.has(feature) ? "delete" : "add"](feature);
        setFeatures(nextFeatures);
    }
    // This is a workaround for a weird bug:
    // The `features` captured in the type select handler below
    // is always the initial empty set. Therefore, we store the current
    // features inside a ref and access it from there.
    const featureContainer = useRef();
    useEffect(() => {
        featureContainer.current = features;
    }, [features]);
    function handleTypeSelect(type) {
        // Use `featureContainer` instead of accessing `features` directly,
        // since it always refers to the initial empty set.
        const features = featureContainer.current || new Set();
        onSelect({
            type,
            accessible: features.has("accessible"),
            cooled: features.has("cooled"),
            dangerousGoods: features.has("dangerousGoods"),
            charger: features.has("charger"),
        });
    }
    return (React.createElement("div", { className: classNames(layout.vertical, styles.container, layout.fill, className) },
        availableFeatures.length > 0 && (React.createElement("div", { className: classNames(layout.horizontalWrap, styles.featureSelection) }, availableFeatures.map(feat => (React.createElement(BoxFeatureSelect, { key: feat, feat: feat, description: t("BoxType:" + feat) || feat, selected: features.has(feat), onClick: handleFeatureClick }))))),
        React.createElement("div", { className: classNames(layout.horizontalWrap, layout.fill, styles.typeSelection, types.length >= 4 && styles.typeSelectionWhenMoreThan4), style: { background: 'inherit' } }, types.map(type => (React.createElement(BoxTypeItem, { key: type, type: type, disabled: !availableTypes.has(type), onSelect: type => handleTypeSelect(type), dimensions: typeBoxDimensions === null || typeBoxDimensions === void 0 ? void 0 : typeBoxDimensions[type], description: t("BoxType:" + type) || type, countItems: types.length }))))));
}
function getFeatureUtilization(utilization, feature, property) {
    var _a;
    return Object.values((_a = utilization[feature]) !== null && _a !== void 0 ? _a : {})
        .reduce((acc, next) => acc + next[property], 0);
}
function getFeatureTotal(utilization, feature) {
    return getFeatureUtilization(utilization, feature, "total");
}
function BoxTypeItem({ type, disabled, onSelect, description, dimensions, countItems }) {
    const handleClick = useCallback(() => onSelect(type), []);
    return (React.createElement("div", { className: classNames(styles.typeItem, countItems >= 4 && styles.typeItemWhenMoreThan4) },
        React.createElement(ListItem, { onClick: handleClick, disabled: disabled },
            React.createElement(ListItemText, { className: styles.itemText },
                React.createElement("div", { className: styles.icon }, getImage(type, disabled)),
                React.createElement(ListItemName, null, description),
                dimensions &&
                    React.createElement(ListItemDescription, null,
                        dimensions.height,
                        " x ",
                        dimensions.width,
                        " x ",
                        dimensions.depth)),
            React.createElement(ListItemIcon, null, MaterialIcon.chevron_right))));
}
function BoxFeatureSelect({ feat, description, selected, onClick }) {
    function handleClick() {
        onClick(feat);
    }
    return (React.createElement(Fragment, null,
        React.createElement(ListItem, { onClick: handleClick, className: classNames(styles.featItem, selected && styles.featItemSelected) },
            React.createElement(ListItemIcon, null,
                React.createElement("div", { className: styles.featIcon }, getFeatureImage(feat))),
            React.createElement(ListItemText, { className: styles.featItemText },
                React.createElement(ListItemName, null, description)),
            React.createElement(ListItemIcon, null,
                React.createElement("div", { className: classNames(styles.featCheckbox, selected && styles.featCheckboxChecked) }, selected ? MaterialIcon.check_box : MaterialIcon.check_box_outline_blank)))));
}
function getFeatureImage(feat, disabled) {
    switch (feat) {
        case 'cooled':
            return React.createElement(CooledIcon, { disabled: disabled });
        case 'accessible':
            return React.createElement(AccessibleIcon, { disabled: disabled });
        case 'dangerousGoods':
            return React.createElement(DangerousGoodsIcon, { disabled: disabled });
        default:
            return React.createElement(StandardBoxIcon, { disabled: disabled });
    }
}
function getImage(name, disabled) {
    switch (name) {
        case "S":
            return React.createElement(SizeIconSmall, { disabled: disabled });
        case "S 32":
            return React.createElement(SizeIconSmall, { slim: true, disabled: disabled });
        case "M":
            return React.createElement(SizeIconMedium, { disabled: disabled });
        case "M 32":
            return React.createElement(SizeIconMedium, { slim: true, disabled: disabled });
        case "L":
            return React.createElement(SizeIconLarge, { disabled: disabled });
        case "L 32":
            return React.createElement(SizeIconLarge, { slim: true, disabled: disabled });
        case "XS":
            return React.createElement(SizeIconExtraSmall, { disabled: disabled });
        case "XL":
            return React.createElement(SizeIconExtraLarge, { disabled: disabled });
        case "XXL":
            return React.createElement(SizeIconExtraExtraLarge, { disabled: disabled });
        case "LG46":
            return React.createElement(LongGoodsIcon, { disabled: disabled });
        case "LG20":
            return React.createElement(LongGoodsSlimIcon, { disabled: disabled });
        case "Hängefach":
        case "Hanging":
        case "SuitBag":
            return React.createElement(HangerIcon, { disabled: disabled });
        case "Cooled":
            return React.createElement(CooledIcon, { disabled: disabled });
        case "Room":
            return React.createElement(RoomIcon, { disabled: disabled });
        default:
            return React.createElement(DefaultIcon, { disabled: disabled });
    }
}
const styles = stylesheet({
    container: {
        background: "var(--color-background-less-contrast)"
    },
    featureSelection: {
        borderBottom: "1px solid var(--color-background)",
        justifyContent: "center"
    },
    typeSelection: {
        padding: "0.75em",
        justifyContent: "center"
    },
    typeSelectionWhenMoreThan4: {
        justifyContent: "start"
    },
    featItem: {
        margin: "0.75em 0.375em",
        outline: '3px solid transparent'
    },
    featItemSelected: {
        outline: '3px solid var(--color-primary)'
    },
    featIcon: {
        width: "2em",
        height: "2em"
    },
    featItemText: {},
    featCheckbox: {
        marginLeft: "0.5rem",
    },
    featCheckboxChecked: {
        color: "var(--color-primary)"
    },
    typeItem: {
        padding: "0.75em",
        minWidth: "10em",
        maxWidth: "20em",
        cursor: 'pointer',
        flex: 1,
    },
    typeItemWhenMoreThan4: {
        flex: "0 0 25%",
    },
    itemText: {
        textAlign: "center",
        paddingBottom: "1em",
    },
    icon: {
        display: "block",
        width: "60%",
        height: "auto",
        margin: "1em auto",
    },
    iconBox: {
        fill: "var(--color-primary)",
        transformOrigin: "center",
    },
    iconBoxDisabled: {
        fill: "var(--color-disabled)",
        transformOrigin: "center",
    },
    iconText: {
        fill: "#fff",
        transformOrigin: "center",
    },
    errorPromptWrapper: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'rgba(0,0,0,0.5)'
    },
    errorPrompt: {
        position: 'relative',
        width: '100%',
        height: '100%',
        maxWidth: '50rem',
        maxHeight: '25rem',
    }
});
function SizeIconSmall({ slim = false, disabled }) {
    return (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 80 124.5", enableBackground: "new 0 0 80 124.5" },
        React.createElement("g", { className: disabled ? styles.iconBoxDisabled : styles.iconBox, transform: slim ? "scale(0.75 1)" : undefined },
            React.createElement("rect", { x: "7.5", y: "75.1", width: "49.4", height: "49.4" }),
            React.createElement("polygon", { points: "76.2,60.2 28.8,60.2 10.8,71.8 58.2,71.8" }),
            React.createElement("polygon", { points: "80,108.3 60.2,121.2 60.2,74.5 80,61.5" })),
        React.createElement("g", { className: styles.iconText, transform: slim ? "scale(0.75 1)" : undefined },
            React.createElement("path", { d: "M40.5,105.3c0,2.3-0.8,4.1-2.5,5.4s-3.9,2-6.8,2s-5.3-0.5-7.1-1.4v-4c1.2,0.6,2.4,1,3.7,1.3s2.5,0.5,3.7,0.5\n\t\t\tc1.7,0,2.9-0.3,3.7-0.9s1.2-1.5,1.2-2.5c0-1-0.4-1.8-1.1-2.4s-2.2-1.5-4.5-2.4c-2.3-0.9-4-2-5-3.3s-1.4-2.7-1.4-4.4\n\t\t\tc0-2.1,0.8-3.8,2.3-5.1s3.6-1.8,6.1-1.8c2.5,0,4.9,0.5,7.3,1.6l-1.3,3.4c-2.3-1-4.3-1.4-6.1-1.4c-1.4,0-2.4,0.3-3.1,0.9\n\t\t\tS28.5,92,28.5,93c0,0.7,0.1,1.2,0.4,1.7s0.7,0.9,1.4,1.3s1.8,1,3.5,1.7c1.9,0.8,3.3,1.5,4.1,2.2s1.5,1.4,1.9,2.3\n\t\t\tS40.5,104.1,40.5,105.3z" }))));
}
function SizeIconMedium({ slim = false, disabled }) {
    return (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 80 124.5", enableBackground: "new 0 0 80 124.5" },
        React.createElement("g", { className: disabled ? styles.iconBoxDisabled : styles.iconBox, transform: slim ? "scale(0.75 1)" : undefined },
            React.createElement("rect", { x: "7.5", y: "55.1", width: "49.4", height: "69.4" }),
            React.createElement("polygon", { points: "76.2,40.2 28.8,40.2 10.8,51.8 58.2,51.8" }),
            React.createElement("polygon", { points: "80,108.3 60.2,121.2 60.2,54.5 80,41.5" })),
        React.createElement("g", { className: styles.iconText, transform: slim ? "scale(0.75 1)" : undefined },
            React.createElement("path", { d: "M30.1,113.2l-7.5-21.5h-0.1c0.2,3.2,0.3,6.2,0.3,9v12.5H19V87.5h5.9l7.1,20.4h0.1l7.3-20.4h5.9v25.7h-4v-12.7\n                c0-1.3,0-2.9,0.1-5s0.1-3.3,0.2-3.7h-0.1l-7.7,21.4H30.1z" }))));
}
const SizeIconLarge = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 80 124.5", enableBackground: "new 0 0 80 124.5" },
    React.createElement("g", { className: disabled ? styles.iconBoxDisabled : styles.iconBox, transform: slim ? "scale(0.75 1)" : undefined },
        React.createElement("rect", { x: "7.5", y: "35.1", width: "49.4", height: "89.4" }),
        React.createElement("polygon", { points: "76.2,20.2 28.8,20.2 10.8,31.8 58.2,31.8" }),
        React.createElement("polygon", { points: "80,108.3 60.2,121.2 60.2,34.5 80,21.5" })),
    React.createElement("g", { className: styles.iconText },
        React.createElement("path", { d: "M25.8,113.2V87.5H30v22.1h10.9v3.6H25.8z" }))));
const SizeIconExtraSmall = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 80 124.5", enableBackground: "new 0 0 80 124.5" },
    React.createElement("g", { className: disabled ? styles.iconBoxDisabled : styles.iconBox },
        React.createElement("rect", { x: "7.5", y: "83.1", width: "49.4", height: "41.4" }),
        React.createElement("polygon", { points: "76.2,68.2 28.8,68.2 10.8,79.8 58.2,79.8" }),
        React.createElement("polygon", { points: "80,108.3 60.2,121.2 60.2,82.5 80,69.5" })),
    React.createElement("g", { className: styles.iconText },
        React.createElement("g", null,
            React.createElement("path", { d: "M52.7,109.3c0,2.3-0.8,4.1-2.5,5.4s-3.9,2-6.8,2s-5.3-0.5-7.1-1.4v-4c1.2,0.6,2.4,1,3.7,1.3c1.3,0.3,2.5,0.5,3.7,0.5\n\t\t\t\tc1.7,0,2.9-0.3,3.7-0.9s1.2-1.5,1.2-2.5s-0.4-1.8-1.1-2.4s-2.2-1.5-4.5-2.4s-4-2-5-3.3s-1.4-2.7-1.4-4.4c0-2.1,0.8-3.8,2.3-5.1\n\t\t\t\ts3.6-1.8,6.1-1.8s4.9,0.5,7.3,1.6L51,95.3c-2.3-1-4.3-1.4-6.1-1.4c-1.4,0-2.4,0.3-3.1,0.9S40.7,96,40.7,97c0,0.7,0.1,1.2,0.4,1.7\n\t\t\t\ts0.7,0.9,1.4,1.3c0.7,0.4,1.8,1,3.5,1.7c1.9,0.8,3.3,1.5,4.1,2.2s1.5,1.4,1.9,2.3S52.7,108.1,52.7,109.3z" })),
        React.createElement("g", null,
            React.createElement("path", { d: "M33.9,116.4h-4.8l-6.4-10.5l-6.5,10.5h-4.5l8.5-13.3l-8-12.4h4.7l5.9,9.7l5.9-9.7h4.6l-8,12.4L33.9,116.4z" })))));
const SizeIconExtraLarge = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 80 124.5", enableBackground: "new 0 0 80 124.5" },
    React.createElement("g", { className: disabled ? styles.iconBoxDisabled : styles.iconBox },
        React.createElement("rect", { x: "7.5", y: "15.1", width: "49.4", height: "109.4" }),
        React.createElement("polygon", { points: "76.2,0.2 28.8,0.2 10.8,11.8 58.2,11.8" }),
        React.createElement("polygon", { points: "80,108.3 60.2,121.2 60.2,14.5 80,1.5" })),
    React.createElement("g", { className: styles.iconText },
        React.createElement("path", { d: "M33.6,113.2h-4.8l-6.4-10.5l-6.5,10.5h-4.5l8.5-13.3l-8-12.4h4.7l5.9,9.7l5.9-9.7H33l-8,12.4L33.6,113.2z" }),
        React.createElement("path", { d: "M37,113.2V87.5h4.2v22.1h10.9v3.6H37z" }))));
const SizeIconExtraExtraLarge = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 80 124.5", enableBackground: "new 0 0 80 124.5" },
    React.createElement("g", { className: disabled ? styles.iconBoxDisabled : styles.iconBox },
        React.createElement("rect", { x: "0", y: "15.1", width: "56.9", height: "109.4" }),
        React.createElement("polygon", { points: "76.2,0.2 21.3,0.2 3.3,11.8 58.2,11.8" }),
        React.createElement("polygon", { points: "80,108.3 60.2,121.2 60.2,14.5 80,1.5" })),
    React.createElement("g", { className: styles.iconText },
        React.createElement("g", null,
            React.createElement("path", { d: "M30.5,113.3h-4.8l-6.4-10.5l-6.5,10.5H8.4l8.5-13.3l-8-12.4h4.7l5.9,9.7l5.9-9.7H30L22,100L30.5,113.3z" }),
            React.createElement("path", { d: "M34,113.3V87.6h4.2v22.1H49v3.6H34z" })),
        React.createElement("g", null,
            React.createElement("path", { d: "M30.5,85.1h-4.8l-6.4-10.5l-6.5,10.5H8.4l8.5-13.3l-8-12.4h4.7l5.9,9.7l5.9-9.7H30l-8,12.4L30.5,85.1z" })))));
const LongGoodsIcon = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 80 124.5", enableBackground: "new 0 0 80 124.5" },
    React.createElement("g", { className: disabled ? styles.iconBoxDisabled : styles.iconBox },
        React.createElement("rect", { x: "30", y: "15.1", width: "20", height: "109.4" }))));
const LongGoodsSlimIcon = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 80 124.5", enableBackground: "new 0 0 80 124.5" },
    React.createElement("g", { className: disabled ? styles.iconBoxDisabled : styles.iconBox },
        React.createElement("rect", { x: "35", y: "15.1", width: "10", height: "109.4" }))));
const HangerIcon = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 100 100", enableBackground: "new 0 0 100 100" },
    React.createElement("circle", { r: 50, cx: 50, cy: 50, className: disabled ? styles.iconBoxDisabled : styles.iconBox }),
    React.createElement("g", { style: { strokeMiterlimit: 10, fill: "none", stroke: "#fff" } },
        React.createElement("polyline", { points: "1.6,70 50,50.6 98.4,70 ", style: { strokeWidth: 3, strokeLinecap: "round", strokeLinejoin: "round" } }),
        React.createElement("path", { d: "M40.6,31c0-5.2,4.2-9.4,9.4-9.4s9.9,2.9,9.4,9.4c-0.3,3.2-5,8-7.3,11.1c-2.3,3.1-2.1,8.5-2.1,8.5", style: { strokeLinejoin: "bevel" } }))));
const DefaultIcon = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 100 100", enableBackground: "new 0 0 100 100" },
    React.createElement("circle", { r: 50, cx: 50, cy: 50, className: disabled ? styles.iconBoxDisabled : styles.iconBox })));
const CooledIcon = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 100 100", enableBackground: "new 0 0 100 100" },
    React.createElement("circle", { r: 50, cx: 50, cy: 50, className: disabled ? styles.iconBoxDisabled : styles.iconBox }),
    React.createElement("g", { className: styles.iconText },
        React.createElement("path", { d: "m 47.285322,21.550314 v 7.088628 l -5.325991,-3.079175 -2.703797,4.667722 8.029788,4.651403 v 10.461574 l -9.019911,-5.211748 -0.01631,-9.31912 -5.396714,0.01087 0.01088,6.190988 -6.158347,-3.557915 -2.698358,4.673161 6.142027,3.547036 -5.331431,3.068291 2.687476,4.678606 8.046109,-4.629644 9.052553,5.228067 -9.019911,5.206309 -8.078751,-4.645963 -2.687476,4.678601 5.364072,3.090054 -6.158347,3.552476 2.698357,4.673162 6.142027,-3.547031 -0.01088,6.152905 5.396714,0.01087 0.01631,-9.281042 9.052553,-5.222628 v 10.412612 l -8.06243,4.673167 2.703797,4.667722 5.358633,-3.100933 v 7.110387 h 5.396714 v -7.088629 l 5.325991,3.079175 2.703797,-4.667722 -8.029788,-4.651403 V 54.659569 l 9.019911,5.211743 0.01631,9.319125 5.396713,-0.01087 -0.01088,-6.190988 6.158347,3.557916 2.698358,-4.673162 -6.142027,-3.547036 5.331431,-3.068296 -2.687476,-4.678601 -8.040669,4.629639 -9.057993,-5.228062 9.025351,-5.211749 8.073311,4.651403 2.687476,-4.678606 -5.364072,-3.090055 6.158347,-3.55247 -2.698357,-4.673161 -6.142027,3.54703 0.01088,-6.152905 -5.396713,-0.01087 -0.01631,9.281043 -9.052552,5.222622 V 34.900656 l 8.062429,-4.673167 -2.703797,-4.667722 -5.358632,3.100933 v -7.110386 z" }))));
const AccessibleIcon = ({ disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 100 100" },
    React.createElement("circle", { r: 50, cx: 50, cy: 50, className: disabled ? styles.iconBoxDisabled : styles.iconBox }),
    React.createElement("g", { className: styles.iconText },
        React.createElement("circle", { cx: "50", cy: "23.484", r: "6.629" }),
        React.createElement("path", { d: "M73.201,53.314l0,-6.628c-5.104,0.066 -10.242,-2.486 -13.49,-6.066l-4.275,-4.74c-0.564,-0.629 -1.26,-1.126 -2.022,-1.491c-0.033,-0 -0.033,-0.033 -0.066,-0.033l-0.034,-0c-1.16,-0.663 -2.485,-0.995 -3.944,-0.862c-3.48,0.298 -5.999,3.381 -5.999,6.861l0,19.588c0,3.646 2.983,6.629 6.629,6.629l16.572,0l0,16.572l6.629,0l0,-18.229c0,-3.646 -2.983,-6.629 -6.629,-6.629l-9.943,0l-0,-11.435c4.276,3.547 10.772,6.43 16.572,6.463Zm-20.45,16.573c-1.359,3.844 -5.038,6.629 -9.38,6.629c-5.502,-0 -9.943,-4.442 -9.943,-9.944c-0,-4.342 2.784,-7.988 6.629,-9.38l-0,-6.861c-7.557,1.525 -13.258,8.22 -13.258,16.241c-0,9.148 7.424,16.572 16.572,16.572c8.021,0 14.716,-5.7 16.241,-13.257l-6.861,-0Z" }))));
const DangerousGoodsIcon = ({ disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 100 100" },
    React.createElement("circle", { r: 50, cx: 50, cy: 50, className: disabled ? styles.iconBoxDisabled : styles.iconBox }),
    React.createElement("g", { className: styles.iconText },
        React.createElement("path", { d: "M15.169,50.006l34.825,34.825l34.837,-34.837l-34.825,-34.825l-34.837,34.837Zm64.143,0l-29.318,29.294l-29.294,-29.306l29.306,-29.306l29.306,29.306" }),
        React.createElement("path", { d: "M49.982,31.948l-0.246,0c-1.37,0.047 -2.68,0.573 -3.7,1.488c-0.391,0.377 -0.664,0.859 -0.787,1.389c-0.037,0.135 -0.037,0.172 -0.037,0.43c0,0.258 0,0.307 0.037,0.455c0.024,0.172 2.397,16.152 2.422,16.423c0.196,1.427 1.509,2.426 2.937,2.237c1.154,-0.17 2.056,-1.082 2.213,-2.237c0.037,-0.332 2.397,-16.276 2.434,-16.435c0.025,-0.148 0.025,-0.197 0.025,-0.431c-0,-0.282 -0,-0.381 -0.062,-0.577c-0.2,-0.712 -0.666,-1.319 -1.303,-1.697c-1.055,-0.713 -2.305,-1.078 -3.577,-1.045l-0.369,0l0.013,0Zm0.245,24.721l-0.147,-0c-1.221,0.051 -2.337,0.699 -2.987,1.733c-0.564,0.837 -0.805,1.851 -0.676,2.852c0.119,1.036 0.619,1.99 1.401,2.68c0.684,0.581 1.549,0.903 2.446,0.909c1.279,-0.021 2.464,-0.668 3.172,-1.733c0.563,-0.847 0.799,-1.868 0.664,-2.876c-0.064,-0.467 -0.197,-0.924 -0.394,-1.353c-0.638,-1.332 -1.976,-2.189 -3.454,-2.212" }))));
const StandardBoxIcon = ({ disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 100 100" },
    React.createElement("circle", { r: 50, cx: 50, cy: 50, className: disabled ? styles.iconBoxDisabled : styles.iconBox }),
    React.createElement("g", { className: styles.iconText },
        React.createElement("rect", { x: "23.515", y: "37.397", width: "36.092", height: "36.092" }),
        React.createElement("path", { d: "M73.708,26.511l-34.631,-0l-13.151,8.475l34.631,-0l13.151,-8.475Z" }),
        React.createElement("path", { d: "M76.485,61.653l-14.466,9.425l-0,-34.119l14.466,-9.498l-0,34.192Z" }))));
const RoomIcon = ({ slim = false, disabled }) => (React.createElement("svg", { version: "1.1", x: "0px", y: "0px", viewBox: "0 0 100 100", enableBackground: "new 0 0 100 100" },
    React.createElement("g", { className: disabled ? styles.iconBoxDisabled : styles.iconBox },
        React.createElement("polygon", { points: "10,40 70,40 70,100 52.5,100 52.5,60 27.5,60 27.5,100 10,100" }),
        React.createElement("polygon", { points: "12.5,35 37.5,20 90,20 72.5,35" }),
        React.createElement("polygon", { points: "75,40 92.5,22.5 92.5,80 75,97.5" }),
        React.createElement("rect", { x: "45", y: "80", width: "5", height: "5" }))));
//# sourceMappingURL=BoxTypeSelection.js.map